<template>
  <div
    v-if="visible"
    class="fixed inset-0 overflow-y-auto modal-background"
    @click="onBackdropClick"
  >
    <div class="flex items-center justify-center min-h-screen">
      <!-- 内容容器：阻止点击冒泡，仅当点击遮罩空白处（modal-background 本身）才关闭 -->
      <div class="modal-container" @click.stop>
        <h2 class="text-lg font-bold mb-4">{{ $t('interface.share.title') }}</h2>

        <!-- 分享人输入框 -->
        <label class="block text-sm font-medium mt-2 mb-2">{{ $t('interface.share.sharer') }}</label>
        <input
          v-model="sharer"
          @input="validateSharerLength"
          type="text"
          :placeholder="$t('interface.share.sharerPlaceholder')"
        />
        <p v-if="sharerError" class="text-red-500 text-sm">{{ sharerError }}</p>

        <!-- 分享语输入框 -->
        <label class="block text-sm font-medium mt-2 mb-2">{{ $t('interface.share.message') }}</label>
        <textarea
          v-model="message"
          @input="validateMessageLength"
          :placeholder="$t('interface.share.messagePlaceholder')"
        ></textarea>
        <p v-if="messageError" class="text-red-500 text-sm">{{ messageError }}</p>

        <!-- 按钮 -->
        <div class="flex justify-between mt-4">
          <button
            @click="submit"
            :class="(sharerError || messageError)
              ? 'btn-disabled px-4 py-2 rounded transition-colors duration-300 flex items-center justify-center'
              : 'btn-primary px-4 py-2 rounded transition-colors duration-300 flex items-center justify-center'"
            :disabled="!!sharerError || !!messageError || isGenerating"
          >
            <span
              v-if="isGenerating"
              class="inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"
            ></span>
            {{ isGenerating ? $t('interface.share.generating') : $t('interface.share.generate') }}
          </button>
          <button @click="close" :disabled="isGenerating" class="text-gray-500">{{ $t('interface.share.cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShareModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 生成卡片进行中：由父组件（grid）控制，禁用按钮并显示 loading
    isGenerating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'close'],
  data() {
    return {
      sharer: '',
      message: '',
      sharerError: '',
      messageError: '',
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // 打开时从 localStorage 恢复分享人，并清空上次残留
        const savedSharer = localStorage.getItem('sharer');
        this.sharer = savedSharer || '';
        this.message = '';
        this.sharerError = '';
        this.messageError = '';
      }
    },
  },
  methods: {
    // 校验分享人输入框的最大长度
    validateSharerLength() {
      this.sharerError = this.sharer.length > 20 ? this.$t('interface.max_20_chars') : '';
    },
    // 校验分享语输入框的最大长度
    validateMessageLength() {
      this.messageError = this.message.length > 400 ? this.$t('interface.max_400_chars') : '';
    },
    // 提交分享，回传 { sharer, message } 给父组件生成卡片
    submit() {
      this.$emit('submit', { sharer: this.sharer, message: this.message });
      // 持久化分享人
      localStorage.setItem('sharer', this.sharer);
      this.message = '';
    },
    // 点击遮罩空白处关闭（内容容器已 @click.stop，不会冒泡到这里）
    onBackdropClick() {
      if (this.isGenerating) return; // 生成中禁止关闭，避免中断导出
      this.close();
    },
    // 关闭弹窗
    close() {
      this.$emit('close');
      this.message = '';
      this.sharerError = '';
      this.messageError = '';
    },
  },
};
</script>

<style scoped>
/* 弹窗遮罩 */
.modal-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.4);
  padding: 20px;
  position: relative;
  z-index: 1002;
  max-width: 500px;
  width: 100%;
  transition: none !important;
  isolation: isolate;
}

.modal-container h2 {
  color: var(--ink, #1b1815);
  font-family: var(--font-display, "Noto Serif SC", serif);
}

/* 输入框样式 */
input[type="text"], textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  font-size: 14px;
  color: #333;
  background-color: #f9f9f9;
  transition: border-color 0.2s ease;
}

input[type="text"]:focus, textarea:focus {
  border-color: var(--brass, #b39a6b);
  outline: none;
}

/* 按钮样式 —— 主操作为墨黑 */
button {
  padding: 10px 20px;
  background-color: var(--ink, #1b1815);
  color: #ffffff;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: #322d27;
}

/* 弹窗主按钮 / 禁用态 */
.btn-primary {
  background-color: var(--ink, #1b1815);
  color: #ffffff;
}

.btn-primary:hover {
  background-color: #322d27;
}

.btn-disabled {
  background-color: #c9c4bb;
  color: #ffffff;
  cursor: not-allowed;
}

button.text-gray-500 {
  background-color: transparent;
  color: var(--text-warm, #6f6a62);
  padding: 10px 20px;
}

button.text-gray-500:hover {
  background-color: rgba(27, 24, 21, 0.05);
}
</style>
